/**
 * WatchModeView — Split pane view for Watch Mode.
 * Left: CDPStreamCanvas (live server browser)
 * Right: UnifiedStepEditor (step list + tabs)
 * Draggable divider between them.
 *
 * Phase 4: Integrated recording support.
 * When recording is active, clicks in CDP view capture selectors and add steps
 * to the step editor in real-time.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Box, Paper, Snackbar, Alert, Chip } from '@mui/material';
import { DragIndicator as DragIcon } from '@mui/icons-material';
import CDPStreamCanvas from './CDPStreamCanvas';
import { UnifiedStepEditor } from '../UnifiedStepEditor';
import useCDPStream from './useCDPStream';
import useRecording from './useRecording';
import { API_BASE_URL } from '../../config';

const WatchModeView = ({
  scriptId,
  scriptName = '',
  // 'playwright' (default, fast clean baseline, supports step mode)
  //   or 'v4' (production-mirror w/ learner extension loaded, no step mode)
  engine = 'playwright',
}) => {
  const [dividerPos, setDividerPos] = useState(55);
  const containerRef = useRef(null);
  const draggingRef = useRef(false);
  const [snackbar, setSnackbar] = useState(null);

  const cdp = useCDPStream(API_BASE_URL, { engine });
  const recording = useRecording();

  // Queue of steps to inject into the editor
  const [pendingSteps, setPendingSteps] = useState([]);

  // When a picked element is received while recording, create a Click step
  useEffect(() => {
    if (recording.isRecording && cdp.pickedElement) {
      const step = recording.recordClick(cdp.pickedElement);
      if (step) {
        // Strip internal _recording metadata before adding to editor
        const { _recording, ...cleanStep } = step;
        setPendingSteps((prev) => [...prev, cleanStep]);
        setSnackbar({
          message: `Recorded: Click ${cdp.pickedElement.selector}`,
          severity: 'success',
        });
      }
      cdp.clearPickedElement();
    }
  }, [cdp.pickedElement, recording, cdp]);

  // Track URL changes to record navigation steps
  const lastUrlRef = useRef('');
  useEffect(() => {
    if (recording.isRecording && cdp.currentUrl && cdp.currentUrl !== lastUrlRef.current) {
      if (lastUrlRef.current) {
        // URL changed — record a navigate step
        const step = recording.recordNavigate(cdp.currentUrl);
        if (step) {
          const { _recording, ...cleanStep } = step;
          setPendingSteps((prev) => [...prev, cleanStep]);
          setSnackbar({
            message: `Recorded: Navigate to ${cdp.currentUrl}`,
            severity: 'info',
          });
        }
      }
      lastUrlRef.current = cdp.currentUrl;
    }
  }, [cdp.currentUrl, recording]);

  // Divider drag handling
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    draggingRef.current = true;

    const handleMouseMove = (moveEvent) => {
      if (!draggingRef.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const newPos = ((moveEvent.clientX - rect.left) / rect.width) * 100;
      if (newPos >= 25 && newPos <= 75) {
        setDividerPos(newPos);
      }
    };

    const handleMouseUp = () => {
      draggingRef.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);

  // Track the next step index for single-step execution
  const nextStepIndexRef = useRef(0);

  // Update next step index when execution progress changes
  useEffect(() => {
    if (cdp.executionProgress && cdp.executionProgress.step) {
      const lastStep = cdp.executionProgress.step; // 1-based
      const status = cdp.executionProgress.status;
      if (status === 'passed') {
        nextStepIndexRef.current = lastStep; // next index (0-based) = last 1-based step
      } else if (status === 'failed') {
        // Stay on the failed step so user can retry
        nextStepIndexRef.current = lastStep - 1;
      }
    }
  }, [cdp.executionProgress]);

  // Reset step index when execution result completes
  useEffect(() => {
    if (cdp.executionResult) {
      nextStepIndexRef.current = 0;
    }
  }, [cdp.executionResult]);

  // Execution callbacks wired to CDP stream
  const handleRunAll = useCallback(
    (steps, variables, credentialId) => {
      if (!cdp.streaming) {
        cdp.startScreencast(scriptId);
        setTimeout(() => {
          cdp.runAll(credentialId, variables);
        }, 5000);
      } else {
        cdp.runAll(credentialId, variables);
      }
    },
    [cdp, scriptId]
  );

  const handleStepForward = useCallback(
    (steps, variables, credentialId) => {
      const stepIndex = nextStepIndexRef.current;
      if (!cdp.streaming) {
        cdp.startScreencast(scriptId);
        setTimeout(() => {
          cdp.runStep(stepIndex, credentialId, variables);
        }, 5000);
      } else {
        cdp.runStep(stepIndex, credentialId, variables);
      }
    },
    [cdp, scriptId]
  );

  const handleCanvasClick = useCallback(
    (x, y) => {
      cdp.sendClick(x, y);
    },
    [cdp]
  );

  const handleRecord = useCallback(
    (shouldRecord) => {
      if (shouldRecord) {
        // Start screencast if not already streaming
        if (!cdp.streaming) {
          cdp.startScreencast(scriptId);
        }
        recording.startRecording();
        setSnackbar({ message: 'Recording started - click elements in the browser to capture steps', severity: 'info' });
      } else {
        recording.stopRecording();
        setSnackbar({
          message: `Recording stopped - ${recording.recordedSteps.length} steps captured`,
          severity: 'success',
        });
      }
    },
    [cdp, scriptId, recording]
  );

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        height: '100%',
        gap: 0,
        userSelect: draggingRef.current ? 'none' : 'auto',
        position: 'relative',
      }}
    >
      {/* Recording indicator */}
      {recording.isRecording && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            bgcolor: '#f44336',
            zIndex: 100,
            animation: 'pulse 1.5s infinite',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.4 },
            },
          }}
        />
      )}

      {/* LEFT PANEL - CDP Screencast */}
      <Paper
        sx={{
          width: `${dividerPos}%`,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          border: recording.isRecording ? '2px solid #f44336' : 'none',
        }}
        elevation={2}
      >
        <CDPStreamCanvas
          frame={cdp.currentFrame}
          metadata={cdp.frameMetadata}
          streaming={cdp.streaming}
          connected={cdp.connected}
          currentUrl={cdp.currentUrl}
          pickedElement={cdp.pickedElement}
          executionProgress={cdp.executionProgress}
          onCanvasClick={handleCanvasClick}
          scriptName={scriptName}
        />
      </Paper>

      {/* DIVIDER */}
      <Box
        onMouseDown={handleMouseDown}
        sx={{
          width: 8,
          cursor: 'col-resize',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: draggingRef.current ? '#1976d2' : '#e0e0e0',
          '&:hover': { bgcolor: '#bbdefb' },
          transition: 'background-color 0.15s',
          flexShrink: 0,
        }}
      >
        <DragIcon sx={{ fontSize: 16, color: '#757575', transform: 'rotate(90deg)' }} />
      </Box>

      {/* RIGHT PANEL - Editor */}
      <Paper
        sx={{
          width: `${100 - dividerPos}%`,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        elevation={2}
      >
        <UnifiedStepEditor
          scriptId={scriptId}
          executionMode="watch"
          onRunAll={handleRunAll}
          onStepForward={handleStepForward}
          onRecord={handleRecord}
          pendingSteps={pendingSteps}
          onPendingStepsConsumed={() => setPendingSteps([])}
          executionProgress={cdp.executionProgress}
          executionResult={cdp.executionResult}
        />
      </Paper>

      {/* Snackbar for recording feedback */}
      <Snackbar
        open={!!snackbar}
        autoHideDuration={3000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {snackbar && (
          <Alert
            severity={snackbar.severity || 'info'}
            onClose={() => setSnackbar(null)}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        )}
      </Snackbar>
    </Box>
  );
};

export default WatchModeView;
